import type { RouteObject } from 'react-router-dom';
import { lazy } from 'react';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';

// Lazy loading dos componentes
const HomePage = lazy(() => import('../pages/home/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const LandingPage = lazy(() => import('../pages/landing/page'));
const RenovarMatriculaPage = lazy(() => import('../pages/renovar-matricula/page'));
const SetupAdminPage = lazy(() => import('../pages/setup-admin/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const MinhaContaPage = lazy(() => import('../pages/minha-conta/page'));
const DetalheTurmaPage = lazy(() => import('../pages/turmas/DetalheTurmaPage'));
const ValidationPage = lazy(() => import('../pages/validar-carteirinha/page'));
const PremiumDashboardPage = lazy(() => import('../pages/dashboard/PremiumDashboard'));

// Páginas
const AlunosPage = lazy(() => import('../pages/alunos/page'));
const CalendarioPage = lazy(() => import('../pages/calendario/page'));
const EstruturaAcademicaPage = lazy(() => import('../pages/estrutura-academica/page'));
const FinanceiroPage = lazy(() => import('../pages/financeiro/page'));
const KanbanPage = lazy(() => import('../pages/kanban/page'));
const MensagensPage = lazy(() => import('../pages/mensagens/page'));

// Estrutura Acadêmica Sub-páginas
const TurmasPage = lazy(() => import('../pages/estrutura-academica/turmas/page'));
const CorpoDocentePage = lazy(() => import('../pages/estrutura-academica/corpo-docente/page'));
const DisciplinasPage = lazy(() => import('../pages/estrutura-academica/disciplinas/page'));
const TurnosPage = lazy(() => import('../pages/estrutura-academica/turnos/page'));
const NiveisEscolaresPage = lazy(() => import('../pages/estrutura-academica/niveis-escolares/page'));


// Configurações
const ConfiguracoesPage = lazy(() => import('../pages/configuracoes/page'));
const ConfiguracoesDadosEscolaPage = lazy(() => import('../pages/configuracoes/dados-escola/page'));
const ConfiguracoesAnoLetivoPage = lazy(() => import('../pages/configuracoes/ano-letivo/page'));
const ConfiguracoesNiveisEscolaresPage = lazy(() => import('../pages/configuracoes/niveis-escolares/page'));
const ConfiguracoesTurnosPage = lazy(() => import('../pages/configuracoes/turnos/page'));
const ConfiguracoesMenusSistemaPage = lazy(() => import('../pages/configuracoes/menus-sistema/page'));
const ConfiguracoesCoresSistemaPage = lazy(() => import('../pages/configuracoes/cores-sistema/page'));
const ConfiguracoesGestaoKanbanPage = lazy(() => import('../pages/configuracoes/gestao-kanban/page'));
const ConfiguracoesGestaoDadosPage = lazy(() => import('../pages/configuracoes/gestao-dados/page'));
const ConfiguracoesUsuariosPage = lazy(() => import('../pages/configuracoes/usuarios/page'));
const ConfiguracoesImagensSitePage = lazy(() => import('../pages/configuracoes/imagens-site/page'));
const IntegracoesPage = lazy(() => import('../pages/configuracoes/integracoes/page'));
const ConfiguracoesCalendarioPage = lazy(() => import('../pages/configuracoes/calendario/page'));
const ConfiguracoesSeriesPage = lazy(() => import('../pages/configuracoes/series/page'));

// Portal do Aluno
const PortalAlunoPage = lazy(() => import('../pages/portal-aluno/page'));
const PortalAlunoPerfilPage = lazy(() => import('../pages/portal-aluno/perfil/page'));
const PortalAlunoBoletimPage = lazy(() => import('../pages/portal-aluno/boletim/page'));
const PortalAlunoInformativosPage = lazy(() => import('../pages/portal-aluno/informativos/page'));
const PortalAlunoFinanceiroPage = lazy(() => import('../pages/portal-aluno/financeiro/page'));
const PortalAlunoMensagensPage = lazy(() => import('../pages/portal-aluno/mensagens/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <LandingPage />
  },
  {
    path: '/dashboard',
    element: <PremiumDashboardPage />
  },
  // ... (lines 56-98 preserved implicitly, editing explicitly below)
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    // Público: a família acessa pelo link do e-mail, sem login.
    path: '/renovar/:token',
    element: <RenovarMatriculaPage />
  },
  {
    path: '/validar-carteirinha',
    element: <ValidationPage />
  },
  {
    path: '/setup-admin',
    element: <SetupAdminPage />
  },
  {
    element: <ProtectedRoute />, // Um único ProtectedRoute para todas as rotas autenticadas
    children: [
      { path: '/home', element: <HomePage /> },
      { path: '/minha-conta', element: <MinhaContaPage /> },
      { path: '/alunos', element: <AlunosPage /> },
      { path: '/estrutura-academica/turmas/:id', element: <DetalheTurmaPage /> },
      { path: '/calendario', element: <CalendarioPage /> },
      { path: '/estrutura-academica', element: <EstruturaAcademicaPage /> },
      { path: '/estrutura-academica/turmas', element: <TurmasPage /> },
      { path: '/estrutura-academica/corpo-docente', element: <CorpoDocentePage /> },
      { path: '/estrutura-academica/disciplinas', element: <DisciplinasPage /> },
      { path: '/estrutura-academica/turnos', element: <TurnosPage /> },
      { path: '/estrutura-academica/niveis-escolares', element: <NiveisEscolaresPage /> },

      { path: '/financeiro', element: <FinanceiroPage /> },
      { path: '/kanban', element: <KanbanPage /> },
      { path: '/mensagens', element: <MensagensPage /> },
      { path: '/configuracoes', element: <ConfiguracoesPage /> },
      { path: '/configuracoes/dados-escola', element: <ConfiguracoesDadosEscolaPage /> },
      { path: '/configuracoes/ano-letivo', element: <ConfiguracoesAnoLetivoPage /> },
      { path: '/configuracoes/niveis-escolares', element: <ConfiguracoesNiveisEscolaresPage /> },
      { path: '/configuracoes/turnos', element: <ConfiguracoesTurnosPage /> },
      { path: '/configuracoes/menus-sistema', element: <ConfiguracoesMenusSistemaPage /> },
      { path: '/configuracoes/cores-sistema', element: <ConfiguracoesCoresSistemaPage /> },
      { path: '/configuracoes/gestao-kanban', element: <ConfiguracoesGestaoKanbanPage /> },
      { path: '/configuracoes/gestao-dados', element: <ConfiguracoesGestaoDadosPage /> },
      { path: '/configuracoes/usuarios', element: <ConfiguracoesUsuariosPage /> },
      { path: '/configuracoes/imagens-site', element: <ConfiguracoesImagensSitePage /> },
      { path: '/configuracoes/integracoes', element: <IntegracoesPage /> },
      { path: '/configuracoes/calendario', element: <ConfiguracoesCalendarioPage /> },
      // Nova rota adicionada
      { path: '/configuracoes/series', element: <ConfiguracoesSeriesPage /> },
      { path: '/portal-aluno', element: <PortalAlunoPage /> },
      { path: '/portal-aluno/perfil', element: <PortalAlunoPerfilPage /> },
      { path: '/portal-aluno/boletim', element: <PortalAlunoBoletimPage /> },
      { path: '/portal-aluno/informativos', element: <PortalAlunoInformativosPage /> },
      { path: '/portal-aluno/financeiro', element: <PortalAlunoFinanceiroPage /> },
      { path: '/portal-aluno/mensagens', element: <PortalAlunoMensagensPage /> },
    ]
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;